<template>
  <div id="app">
    <dashboard
      v-if="isAuthenticated"
      :hasNotification="hasNotification"
      :notification="notification"
    />
    <router-view v-else />

    <TheGallery ref="galleryRef" />
  </div>
</template>

<script lang="ts">
import { mapActions, mapGetters, mapState } from "vuex"

import TheGallery from "@/components/Common/TheGallery/TheGallery.vue"
import Dashboard from "@/components/layout/Dashboard.vue"
import { REFRESH_TOKEN } from "@/store/modules/auth/actionTypes"
import { LOGOUT } from "@/store/modules/auth/actionTypes"
import { GET_BALANCE, GET_USER_DATA } from "@/store/modules/user/actionTypes"
import { getCookie } from "@/utils/cookies"
import {
  ADS_API,
  API,
  getADSResponseInterceptor,
  getModerationResponseInterceptor,
  getRequestInterceptor,
  getResponseInterceptor,
  PM_API,
  TM_API
} from "@/utils/http"

const UNREAD_MESSAGES_CHECK_DELAY = 5000

export default {
  metaInfo() {
    return {
      title: this.pageTitle,
      link: [
        { rel: "icon", type: "image/x-icon", href: `${this.isIconPrefix}favicon.ico` },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: `${this.isIconPrefix}favicon-16x16.png`
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: `${this.isIconPrefix}favicon-32x32.png`
        },
        { rel: "icon", type: "image/svg+xml", sizes: "any", href: `${this.isIconPrefix}icon.png` }
      ]
    }
  },

  components: {
    Dashboard,
    TheGallery
  },

  data() {
    return {
      refreshTokenIntervalId: null,
      checkConnectionIntervalId: null,
      timerIcon: null,
      changeIcon: false,
      unreadMessagesInterval: undefined
    }
  },

  created() {
    if (this.$workbox) {
      this.$workbox.addEventListener("waiting", () => {
        this.showUpgradeUI = true
      })
    }

    clearInterval(this.refreshTokenIntervalId)

    this.refreshTokenIntervalId = setInterval(() => this.forceRefreshToken(), 1000 * 60 * 15)

    API.interceptors.request.use(getRequestInterceptor.bind(this))
    API.interceptors.response.use(undefined, getResponseInterceptor.bind(this))

    ADS_API.interceptors.response.use(undefined, getADSResponseInterceptor.bind(this))

    TM_API.interceptors.request.use(getRequestInterceptor.bind(this))
    TM_API.interceptors.response.use(undefined, getADSResponseInterceptor.bind(this))

    PM_API.interceptors.request.use(getRequestInterceptor.bind(this))
    PM_API.interceptors.response.use(undefined, getModerationResponseInterceptor.bind(this))
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      async handler(isLogged) {
        if (isLogged) {
          this[GET_USER_DATA]()
          this[GET_BALANCE]()
          this.checkUnreadMessages()
          this.unreadMessagesInterval = setInterval(
            () => this.checkUnreadMessages(),
            UNREAD_MESSAGES_CHECK_DELAY
          )
        } else {
          if (this.unreadMessagesInterval) {
            clearInterval(this.unreadMessagesInterval)
            this.unreadMessagesInterval = undefined
          }

          if (this.$route.path !== "/sign-in") {
            this.$router.push("/sign-in")
          }
        }
      }
    },
    unreadMessagesCount(count) {
      if (count > 0) {
        if (!this.timerIcon) {
          this.animIcon()
        }

        return
      }

      clearTimeout(this.timerIcon)

      this.timerIcon = null
      this.changeIcon = false
    },
    "$route.name": {
      async handler(routeName) {
        // Watcher для оновлення балансу при переході в Кабінет або Комісію по товарах(він відображається на цих сторінках)
        if (["dashboard", "cpaStatistics"].includes(routeName)) {
          this[GET_BALANCE]()
        }
      }
    }
  },

  methods: {
    ...mapActions("auth", [REFRESH_TOKEN, LOGOUT]),
    ...mapActions("messenger", ["checkUnreadMessages"]),
    ...mapActions("user", [GET_USER_DATA, GET_BALANCE]),

    async refreshToken() {
      const refreshToken = getCookie("auth_refresh_mpo")
      const accessToken = getCookie("auth_access_mpo")
      let token = null

      try {
        /**
         * If a request for updating the token has already been sent,
         * we awaiting receive a token
         */
        if (this.tokenRefreshing) {
          const access = await this.awaitRefreshToken()

          token = access
        }

        if (refreshToken && !accessToken && !token) {
          const data = await this[REFRESH_TOKEN](refreshToken)
          const { access } = data

          token = access
        }
      } catch (e) {
        this[LOGOUT]()

        throw new Error(e)
      }

      return token
    },

    async forceRefreshToken() {
      const refreshToken = getCookie("auth_refresh_mpo")

      try {
        if (refreshToken) await this[REFRESH_TOKEN](refreshToken)
      } catch (e) {
        this[LOGOUT]()
      }
    },

    async accept() {
      this.showUpgradeUI = false

      await this.$workbox.messageSW({ type: "SKIP_WAITING" })
    },

    async awaitRefreshToken() {
      const token = await new Promise((resolve, reject) => {
        let iteration = 120
        const isRefreshed = () => this.tokenRefreshing === false
        const intervalId = setInterval(() => {
          const accessToken = getCookie("auth_access_mpo")

          iteration = iteration - 1

          if (accessToken && isRefreshed()) {
            clearInterval(intervalId)
            resolve(accessToken)
          } else if ((!accessToken && isRefreshed()) || iteration === 0) {
            clearInterval(intervalId)
            reject(null)
          }
        }, 250)
      })

      return token
    },

    animIcon() {
      this.timerIcon = setTimeout(() => {
        this.changeIcon = !this.changeIcon
        this.animIcon()
      }, 2000)
    },

    showGallery(payload) {
      this.$refs.galleryRef.handleEvent(payload)
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),

    ...mapGetters("user", ["notification"]),

    ...mapState("user", {
      profile: (state: any): any => state.profile
    }),

    ...mapState("auth", ["sellerUuid", "tokenRefreshing"]),

    ...mapState("messenger", {
      unreadMessagesCount: (state: any): any => state.unreadMessages
    }),

    hasNotification(): boolean {
      return !!this.notification.type
    },

    computedUser(): any {
      return {
        ...this.profile,
        uuid: this.sellerUuid,
        role: "seller"
      }
    },

    layout() {
      const { layout = "dashboard" } = this.$route.meta

      return `layout-${layout}`
    },

    shopName() {
      return this.profile["name"] || ""
    },

    pageTitle() {
      if (this.changeIcon && this.unreadMessagesCount) {
        return this.$tc("messenger.unread_message", this.unreadMessagesCount, {
          count: this.unreadMessagesCount
        })
      } else {
        return this.shopName ? this.shopName : this.$t("app.title")
      }
    },

    isIconPrefix() {
      return this.changeIcon ? "m-" : ""
    }
  },

  provide() {
    return {
      showGallery: this.showGallery
    }
  }
}
</script>

<style lang="scss" scoped>
#app {
  min-width: 960px !important;
  min-height: 100%;
  position: relative;
}
</style>
